// The agent pane's context, hook and labels, kept apart from the provider so the provider
// file exports only components (react-refresh/only-export-components).
import { createContext, type ReactNode, useContext } from "react";
import type { HeldAttachments } from "../api/hooks/useAttachments";

import { useProject } from "../api/hooks/useProject";
import type {
  ActionDraft,
  ActionIntent,
} from "../api/hooks/useProjectAgentStatus";
import { projectDisplayName } from "../utils/projectName";
import { agentDisplayLabel } from "../utils/runParticipants";

/** A conversation about one next action, as a button asked for it. */
export interface PaneRequest {
  projectId: string;
  /** The agent slot the action is in; its conversation runs there too. */
  config: string;
  agent: string;
  actionId: string;
  intent: ActionIntent;
  /** The action's title, the pane's heading. */
  title: string;
  /** What kind of action it is, as the page names it: email, task, decision. */
  kind: string;
  /** The action as the page shows it opened, the first thing in the conversation. */
  context: ReactNode;
  /** The conversation this button already started, as the page knows it. */
  runId: string | null;
  /** A draft no conversation wrote (a scheduled pass did), shown above the conversation. */
  email: ActionDraft | null;
}

export interface PaneConversation extends PaneRequest {
  tab: "conversation";
  key: string;
}

/** Where one agent stands on one project, opened from its name on an action or an
 * answer. See docs/Y2026-Q4/design-agent-peek.md. */
export interface PeekRequest {
  projectId: string;
  /** The agent slot whose state it reads. */
  config: string;
  agent: string;
}

export interface PanePeek extends PeekRequest {
  tab: "peek";
  key: string;
}

export type PaneItem = PaneConversation | PanePeek;

/** A run being started for a conversation, or the error that stopped it. */
export interface PaneStart {
  pending: boolean;
  error: string | null;
}

export interface AgentPane {
  /** The tabs, conversations and peeks, in the order they were opened. */
  items: PaneItem[];
  activeKey: string | null;
  minimised: boolean;
  open: (request: PaneRequest) => void;
  peek: (request: PeekRequest) => void;
  close: (key: string) => void;
  select: (key: string) => void;
  setMinimised: (minimised: boolean) => void;
  /** The run a button started from the pane this session. */
  startedRun: (
    projectId: string,
    config: string,
    actionId: string,
    intent: ActionIntent,
  ) => string | null;
  /** The run a pane conversation is on: the page's, or the one the pane started. */
  runOf: (conversation: PaneConversation) => string | null;
  startOf: (key: string) => PaneStart;
  /** Whether the agent is working in the conversation, for its tab. */
  workingIn: (key: string) => boolean;
  setWorking: (key: string, working: boolean) => void;
  /** Starts the conversation's run, once: a second call while one is in flight or
   * after one exists does nothing. Discuss passes the operator's first message. */
  start: (
    conversation: PaneConversation,
    first?: { content: string } & HeldAttachments,
  ) => void;
}

export const paneKey = (
  projectId: string,
  config: string,
  actionId: string,
  intent: ActionIntent,
) => `${projectId}/${config}/${actionId}/${intent}`;

export const peekKey = ({ projectId, config, agent }: PeekRequest) =>
  `peek/${projectId}/${config}/${agent}`;

export const NO_START: PaneStart = { pending: false, error: null };

export const AgentPaneContext = createContext<AgentPane | null>(null);

export const useAgentPane = (): AgentPane => {
  const pane = useContext(AgentPaneContext);
  if (!pane) throw new Error("useAgentPane outside AgentPaneProvider");
  return pane;
};

export const paneTag = (c: PaneItem): string =>
  c.tab === "peek"
    ? `${agentDisplayLabel(c.agent)} at a glance`
    : c.intent === "draft_email"
      ? "Draft email"
      : c.intent === "draft_reminder"
        ? "Draft reminder"
        : `Discuss ${c.kind}`;

/** What the conversation is doing, as the pane's header says it in a sentence. */
export const paneActivity = (c: PaneConversation): string =>
  c.intent === "draft_email"
    ? "Drafting an email"
    : c.intent === "draft_reminder"
      ? "Drafting a reminder"
      : `Discussing this ${c.kind}`;

/** The project's name for a pane label, the id until the project has loaded. */
export const usePaneProjectName = (projectId: string): string => {
  const project = useProject(projectId).data;
  return project ? projectDisplayName(project) : projectId;
};
